/**
 * T8004 · Connectivity-driven refetch fan-out.
 *
 * SPEC ANCHOR: `specs/009-data-fetching-audit/05-new-infra-sdk-changes.html`
 * §L1447 — fixes verified TanStack bug #6: `invalidateQueries({refetchType:'none'})`
 * does NOT force re-evaluation of function-form `staleTime`/`refetchInterval`.
 *
 * On every `connectivity:change` transition we need to refresh every
 * SDK-managed realtime query so missed events during the disconnect
 * are caught up via HTTP. The naïve approach —
 * `qc.refetchQueries({ predicate: q => q.meta?.realtime })` — does NOT
 * work because TanStack's `Query.fetch()` uses `Query.options.queryFn`
 * (the last observer to call `setOptions` wins, which is typically a
 * subscriber sentinel in our SDK). The sentinel resolves with cached
 * data and no HTTP fires.
 *
 * `refetchAllActiveRealtimeQueries` iterates each Query's observers,
 * finds the data-owning one (non-sentinel), and calls ITS `refetch()`
 * so the parent's real queryFn runs. The predicate is forwarded to
 * keep non-SDK queries the consumer mounted (e.g. their own
 * `useQuery` without `meta.realtime`) untouched.
 */

import type { Query, QueryClient } from '@tanstack/react-query';

import { hasActiveFetchOnceRealtimeOwner } from '../../core/internal/fetch-once-realtime-policy.js';
import { refetchAllActiveRealtimeQueries } from '../../core/internal/refetchRealtimeQuery.js';
import type { ConnTransition } from '../fsm/state-machine.js';

/**
 * Fire once per `connectivity:change` transition. Touches every
 * currently-mounted realtime query so each observer's parent refetch
 * runs (which re-reads function-form `staleTime` / `refetchInterval`
 * AND fires a real HTTP request).
 *
 * `t` is accepted but currently unused — kept so downstream telemetry
 * can read the transition context if it grows past a simple fan-out.
 *
 * Returns synchronously (refetches fire in parallel). Resolves the
 * Promise immediately so callers awaiting it move on; the actual
 * refetches resolve independently via TanStack's standard observer
 * notification.
 */
export function fanOutRealtimeRefetch(qc: QueryClient, _t: ConnTransition): Promise<void> {
  refetchAllActiveRealtimeQueries(qc, (query) => shouldRecoveryRefetchRealtimeQuery(qc, query));
  return Promise.resolve();
}

/** True when connectivity recovery is allowed to issue another HTTP snapshot. */
export function shouldRecoveryRefetchRealtimeQuery(
  queryClient: QueryClient,
  query: Query,
): boolean {
  return (
    query.meta?.realtime === true &&
    query.meta?.pollingManaged !== true &&
    !hasActiveFetchOnceRealtimeOwner(queryClient, query.queryKey)
  );
}

/** Spec-anchor pin: emitted alongside the fan-out for Better Stack pivot. */
export const FAN_OUT_SPEC_LINE = 'L1447';
